import { Skeleton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api";
import { Text } from "@/components/Text";
import type { PreviewFile } from "./previewFile";

// PDF.js is large; load it only when someone opens a preview.
const PdfPreview = lazy(() => import("./PdfPreview"));

function PreviewSkeleton() {
  return <Skeleton className="aspect-[1/1.294] w-full rounded-md" />;
}

/** Checks that can't run right now (the preparation runtime or the virus scanner is down). */
const CHECKS_UNAVAILABLE = new Set(["STARTER_UNAVAILABLE", "VALIDATION_UNAVAILABLE"]);
const RECHECK_MS = 2_000;
const RECHECK_MAX_MS = 30_000;

/** The file is still being checked (a file from before checks were on is checked on its first preview). */
function beingChecked(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409 && error.code === "ATTACHMENT_PREPARING";
}

/**
 * A document rendered as pages. The server sends PDFs as they are and converts
 * Word, Excel and PowerPoint files to PDF (that conversion needs LibreOffice on the server).
 * While the file is being checked it says so and asks again: on the chat's live file notice
 * (useChatFileEvents), and otherwise with backoff.
 */
export function DocumentPreview({ file }: Readonly<{ file: PreviewFile }>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const preview = useQuery({
    queryKey: [...file.key, "pdf"],
    queryFn: file.pdf,
    // A version's file never changes; conversion is slow, so don't redo it.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchInterval: (query) =>
      beingChecked(query.state.error)
        ? Math.min(RECHECK_MS * 2 ** Math.max(0, query.state.errorUpdateCount - 1), RECHECK_MAX_MS)
        : false,
  });

  if (preview.isPending) return <PreviewSkeleton />;
  if (preview.isError && beingChecked(preview.error)) {
    const unavailable = CHECKS_UNAVAILABLE.has(preview.error.reasonCode ?? "");
    return (
      <output className="block rounded-lg border border-border-subtle bg-surface-secondary px-4 py-6 text-center">
        <Text tone="secondary">{t(unavailable ? "previewChecksUnavailable" : "previewChecking")}</Text>
      </output>
    );
  }
  if (preview.isError) {
    const status = preview.error instanceof ApiError ? preview.error.status : undefined;
    let message = t("previewError");
    if (status === 415) message = t("previewUnsupported");
    else if (status === 503) message = t("previewUnavailable");
    return (
      <output className="block rounded-lg border border-border-subtle bg-surface-secondary px-4 py-6 text-center">
        <Text tone="secondary">{message}</Text>
      </output>
    );
  }
  return (
    <Suspense fallback={<PreviewSkeleton />}>
      <PdfPreview file={preview.data.blob} />
    </Suspense>
  );
}
